import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Mail,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Shield,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";
import loginRequests from "../../../api/Customer/LoginRequests";

const ForgotPassword = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [userSlug, setUserSlug] = useState("");
  const [isResendEmail, setIsResendEmail] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    
  }, [countdown]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setStatus(null);

      const payload = {
        emailorPhone: values.email,
      };

      const response = await loginRequests.sendPasswordResetEmail(payload);

      if (response.success) {
        setSubmittedEmail(values.email);
        setUserSlug(response.data.data.data.user_slug);
        setEmailSent(true);
        setCountdown(60);
        toast.success(
          response.data.message ||
            "Password reset instructions have been sent to your email!"
        );
      } else {
        const errorData = response.error;
        console.error("Forgot Password Error:", errorData);
        console.error("Error details:", errorData.data.reason);

        if (errorData.errors) {
          Object.keys(errorData.errors).forEach((field) => {
            const fieldMap = {
              email: "email",
            };

            const formField = fieldMap[field] || field;
            setFieldError(formField, errorData.errors[field][0]);
          });
        } else if (errorData.data.reason) {
          if (errorData.data.reason.toLowerCase().includes("email")) {
            setFieldError("email", errorData.data.reason);
          } else {
            setStatus(errorData.reason);
            toast.error(errorData.data.reason);
          }
        } else {
          setStatus(
            "Failed to send reset email. Please check your email and try again."
          );
          toast.error(
            "Failed to send reset email. Please check your email and try again."
          );
        }
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setStatus("An unexpected error occurred. Please try again.");
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResendEmail(true);
    try {
      const payload = {
        emailorPhone: submittedEmail,
      };
      const response = await loginRequests.sendPasswordResetEmail(payload);

      if (response.success) {
        setIsResendEmail(false);
        setCountdown(60);
        toast.success(
          response.data.message ||
            "Password reset instructions have been sent again!"
        );
      } else {
        setIsResendEmail(false);
        toast.error(
          response.error.message || "Failed to resend email. Please try again."
        );
      }
    } catch (error) {
      console.error("Resend email error:", error);
      setIsResendEmail(false);
      toast.error("Failed to resend email. Please try again.");
    }
  };

  const handleTryDifferentEmail = () => {
    setEmailSent(false);
    setSubmittedEmail("");
    setCountdown(0);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div
        className={`hidden md:flex w-2/5 bg-gradient-to-br from-blue-600 to-indigo-600 text-gray-800 p-16 flex-col justify-between fixed h-screen overflow-hidden transition-opacity duration-500 ease-in-out ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div>
          <h1 className="text-4xl text-white font-bold mb-6">Reset Your Password</h1>
          <p className="text-lg text-white opacity-90 mb-8">
            Don't worry, we'll help you get back into your account securely.
          </p>

          <div className="space-y-6 mt-10">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg text-white">Email Verification</h3>
                <p className="opacity-80 text-sm text-white">
                  We'll send reset instructions to your registered email
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg text-white">Secure Process</h3>
                <p className="opacity-80 text-sm text-white">
                  Your account security is our top priority
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg text-white">Quick Recovery</h3>
                <p className="opacity-80 text-sm text-white">
                  Get back to your account in just a few minutes
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <p className="text-sm opacity-80">
            Remember your password?{" "}
            <Link
              to="/login"
              className="font-medium underline hover:text-white transition-all duration-300"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right section with form - full width on mobile, adjusted for fixed sidebar on desktop */}
      <div
        className={`w-full md:w-3/5 p-8 md:p-16 md:ml-[40%] bg-white transition-transform duration-500 ease-in-out ${
          isVisible ? "translate-x-0" : "translate-x-8"
        }`}
      >
        <div className="w-full max-w-md mx-auto">
          {/* Mobile-only header with sign in link */}
          <div className="md:hidden text-center mb-8">
            <p className="text-sm text-gray-600">
              Remember your password?{" "}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500 transition-all duration-300"
              >
                Sign in here
              </Link>
            </p>
          </div>
          {!emailSent ? (
            <>
              <div
                className={`text-center text-gray-800 md:text-left mb-10 transition-transform duration-500 ease-in-out ${
                  isVisible ? "translate-y-0" : "translate-y-4"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800">
                  Forgot your password?
                </h2>
                <p className="text-gray-600 mt-2">
                  Enter your email address and we'll send you instructions to
                  reset your password
                </p>
              </div>

              <Formik
                initialValues={{ email: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, status }) => (
                  <Form
                    className={`space-y-6 transition-opacity duration-700 ease-in-out ${
                      isVisible ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {/* General error message */}
                    {status && (
                      <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700">{status}</p>
                      </div>
                    )}

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <Field
                          type="email"
                          name="email"
                          id="email"
                          disabled={isSubmitting}
                          className="pl-10 block w-full rounded-lg border border-gray-300 py-3 shadow-sm focus:border-blue-500 focus:ring-blue-500 outline-none transition-all duration-300 disabled:bg-gray-50 disabled:text-gray-500"
                          placeholder="your@email.com"
                        />
                      </div>
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="mt-1 text-sm text-red-600 flex items-center space-x-1"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium shadow-md transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending instructions...
                        </>
                      ) : (
                        <>
                          Send reset instructions
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-150" />
                        </>
                      )}
                    </button>

                    <div className="mt-6 text-center">
                      <Link
                        to="/login"
                        className="flex items-center justify-center space-x-1 text-sm text-gray-600 hover:text-gray-800 transition-all duration-300"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back to sign in</span>
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            // Success State
            <div
              className={`transition-all duration-500 ease-in-out ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  Check your email
                </h2>
                <p className="text-gray-600">
                  We've sent password reset instructions to
                </p>
                <p className="text-blue-600 font-medium mt-1">
                  {submittedEmail}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">What's next?</p>
                    <ul className="space-y-1 text-blue-600">
                      <li>• Check your email inbox</li>
                      <li>• Click the reset link in the email</li>
                      <li>• Create a new password</li>
                      <li>• Sign in with your new password</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Link
                  to="/reset-password"
                  state={{ email: submittedEmail, user_slug: userSlug }}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>Proceed to Reset Password</span>
                </Link>

                <button
                  onClick={handleResendEmail}
                  disabled={countdown > 0 || isResendEmail}
                  className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {countdown > 0 ? (
                    <>
                      <Clock className="w-4 h-4" />
                      <span>Resend in {countdown}s</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw
                        className={`w-4 h-4 ${
                          isResendEmail ? "animate-spin" : ""
                        }`}
                      />
                      <span>Resend email</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleTryDifferentEmail}
                  className="w-full py-3 px-4 text-blue-600 hover:text-blue-700 font-medium transition-all duration-300"
                >
                  Try a different email address
                </button>
              </div>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Didn't receive the email? Check your spam folder or contact
                  support.
                </p>
                <Link
                  to="/login"
                  className="flex items-center justify-center space-x-1 text-sm text-gray-600 hover:text-gray-800 transition-all duration-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to sign in</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
